import React, { useState } from 'react';
import { formatCEP } from '../../../utils/inputMasks';
import { useFormValidation } from '../../../hooks/useFormValidation';
import { addressValidationRules } from './validations/Address';
import { CepService } from '../../../services/cepService';

interface Customer {
  name: string;
  email: string;
  phoneNumber: string;
  document: {
    type: string;
    number: string;
    country: string;
  };
  address: {
    zipCode: string;
    street: string;
    streetNumber: string;
    complement: string;
    district: string;
    city: string;
    state: string;
    country: string;
  };
}

interface AddressFormProps {
  customer: Customer;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onSubmit: (customer: Customer) => void;
  onBack: () => void;
}

const AddressForm: React.FC<AddressFormProps> = ({
  customer,
  onInputChange,
  onSubmit,
  onBack
}) => {
  const [isLoadingCep, setIsLoadingCep] = useState(false);
  const [cepError, setCepError] = useState<string | null>(null);
  
  const { errors, validateFieldOnChange, isFormValid } = useFormValidation(
    addressValidationRules
  );

  const handleCepLookup = async (cep: string) => {
    if (!CepService.validateCepFormat(cep)) {
      return;
    }

    try {
      setIsLoadingCep(true);
      setCepError(null);
      
      const addressData = await CepService.fetchAddressByCep(cep);
      
      if (addressData) {
        const updateField = (name: string, value: string) => {
          const syntheticEvent = {
            target: { name, value }
          } as React.ChangeEvent<HTMLInputElement>;
          handleInputChangeWithValidation(syntheticEvent);
        };

        updateField('address.street', addressData.street);
        updateField('address.district', addressData.district);
        updateField('address.city', addressData.city);
        updateField('address.state', addressData.state);
        if (addressData.complement) {
          updateField('address.complement', addressData.complement);
        }
      }
    } catch (error) {
      setCepError(error instanceof Error ? error.message : 'Erro ao buscar CEP');
    } finally {
      setIsLoadingCep(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isFormValid(customer.address)) {
      onSubmit(customer);
    }
  };

  const handleInputChangeWithValidation = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const fieldName = name.startsWith('address.') ? name.substring(8) : name;
    validateFieldOnChange(fieldName as keyof typeof customer.address, value);
    if (name === 'address.zipCode') {
      const formattedValue = formatCEP(value);
      if (formattedValue.length === 9) {
        handleCepLookup(formattedValue);
      }
    }
    onInputChange(e);
  };
  return (
    <>
      <p className="text-sm text-gray-600 mb-4">Por favor, preencha seus dados de endereço.</p>
      
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <div className="mb-3 relative">
            <label htmlFor="address.zipCode" className="block text-xs font-semibold text-gray-700 mb-1">CEP</label>
            <div className="relative">
              <input
                id="address.zipCode"
                type="text"
                name="address.zipCode"
                value={customer.address.zipCode}
                onChange={handleInputChangeWithValidation}
                required
                className={`input ${errors['zipCode'] ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="00000-000"
              />
              {isLoadingCep && (
                <div className="absolute top-1/2 right-3 -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500"></div>
                </div>
              )}
            </div>
            {(errors['zipCode'] || cepError) && (
              <p className="text-red-500 text-xs mt-1">{errors['zipCode'] || cepError}</p>
            )}
            {!errors['zipCode'] && !cepError && customer.address.zipCode.length === 9 && !isLoadingCep && (
              <p className="text-green-500 text-xs mt-1">CEP encontrado!</p>
            )}
          </div>
          
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2 mb-3">
              <label htmlFor="address.street" className="block text-xs font-semibold text-gray-700 mb-1">Rua</label>
              <input
                id="address.street"
                type="text"
                name="address.street"
                value={customer.address.street}
                onChange={handleInputChangeWithValidation}
                required
                className={`input ${errors['street'] ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Nome da rua"
              />
              {errors['street'] && (
                <p className="text-red-500 text-xs mt-1">{errors['street']}</p>
              )}
            </div>
            
            <div className="mb-3">
              <label htmlFor="address.streetNumber" className="block text-xs font-semibold text-gray-700 mb-1">Número</label>
              <input
                id="address.streetNumber"
                type="text"
                name="address.streetNumber"
                value={customer.address.streetNumber}
                onChange={handleInputChangeWithValidation}
                required
                className={`input ${errors['streetNumber'] ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="123"
              />
              {errors['streetNumber'] && (
                <p className="text-red-500 text-xs mt-1">{errors['streetNumber']}</p>
              )}
            </div>
          </div>
          
          <div className="mb-3">
            <label htmlFor="address.complement" className="block text-xs font-semibold text-gray-700 mb-1">Complemento</label>
            <input
              id="address.complement"
              type="text"
              name="address.complement"
              value={customer.address.complement}
              onChange={handleInputChangeWithValidation}
              className="input"
              placeholder="Apto 101"
            />
          </div>
          
          <div className="mb-3">
            <label htmlFor="address.district" className="block text-xs font-semibold text-gray-700 mb-1">Bairro</label>
            <input
              id="address.district"
              type="text"
              name="address.district"
              value={customer.address.district}
              onChange={handleInputChangeWithValidation}
              required
              className={`input ${errors['district'] ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="Centro"
            />
            {errors['district'] && (
              <p className="text-red-500 text-xs mt-1">{errors['district']}</p>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <div className="mb-3">
              <label htmlFor="address.city" className="block text-xs font-semibold text-gray-700 mb-1">Cidade</label>
              <input
                id="address.city"
                type="text"
                name="address.city"
                value={customer.address.city}
                onChange={handleInputChangeWithValidation}
                required
                className={`input ${errors['city'] ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="São Paulo"
              />
              {errors['city'] && (
                <p className="text-red-500 text-xs mt-1">{errors['city']}</p>
              )}
            </div>
            
            <div className="mb-3">
              <label htmlFor="address.state" className="block text-xs font-semibold text-gray-700 mb-1">Estado</label>
              <select
                id="address.state"
                name="address.state"
                value={customer.address.state}
                onChange={handleInputChangeWithValidation}
                required
                className={`input ${errors['state'] ? 'border-red-500' : 'border-gray-300'} appearance-none`}
              >
                <option value="">Selecione</option>
                <option value="AC">AC</option>
                <option value="AL">AL</option>
                <option value="AP">AP</option>
                <option value="AM">AM</option>
                <option value="BA">BA</option>
                <option value="CE">CE</option>
                <option value="DF">DF</option>
                <option value="ES">ES</option>
                <option value="GO">GO</option>
                <option value="MA">MA</option>
                <option value="MT">MT</option>
                <option value="MS">MS</option>
                <option value="MG">MG</option>
                <option value="PA">PA</option>
                <option value="PB">PB</option>
                <option value="PR">PR</option>
                <option value="PE">PE</option>
                <option value="PI">PI</option>
                <option value="RJ">RJ</option>
                <option value="RN">RN</option>
                <option value="RS">RS</option>
                <option value="RO">RO</option>
                <option value="RR">RR</option>
                <option value="SC">SC</option>
                <option value="SP">SP</option>
                <option value="SE">SE</option>
                <option value="TO">TO</option>
              </select>
              {errors['state'] && (
                <p className="text-red-500 text-xs mt-1">{errors['state']}</p>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex flex-col gap-3 mt-4 w-full">
          <button
            type="submit"
            disabled={!isFormValid(customer.address)}
            className={`w-full btn btn-primary ${!isFormValid(customer.address) ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Prosseguir para pagamento
          </button>
          <button
            type="button"
            onClick={onBack}
            className="w-full btn border border-green-600 text-green-600 hover:bg-green-50"
          >
            Voltar
          </button>
        </div>
      </form>
    </>
  );
};

export default AddressForm;
